import seoConfig from 'src/configs/seo';

/**
 * Builds consistent search and social metadata. Only explicitly supplied canonical URLs are published, so an account
 * address or a private token can never become a preview URL by falling back to the current route.
 *
 * @param {Object} options - Page metadata.
 * @param {string} options.title - The page title, with or without the site name.
 * @param {string} options.description - A plain-text description of the page.
 * @param {string|null} [options.canonicalUrl] - The public canonical URL, absent for private or unresolved pages.
 * @param {string} [options.robots] - Indexing policy; private pages must explicitly use noindex.
 * @param {string|null} [options.imageUrl] - Preview image, falling back to the current site logo.
 * @param {string} [options.imageAlt] - Preview image description.
 * @param {string} [options.type] - Open Graph page type.
 * @param {boolean} [options.isHomePage] - Whether to publish the site's identity on its home page.
 * @returns {Object} - Metadata ready for usePageMeta's escaping boundary.
 */
export function buildPageMeta(options) {
    const {
        title,
        description,
        canonicalUrl = null,
        robots = 'index,follow',
        imageUrl,
        imageAlt = seoConfig.imageAlt,
        type = 'website',
        isHomePage = false
    } = options;
    const socialTitle = formatPageTitle(title);
    const resolvedImage = imageUrl || seoConfig.imageUrl;
    return {
        title,
        meta: {
            description: { name: 'description', content: description },
            robots: { name: 'robots', content: robots },
            ogTitle: { property: 'og:title', content: socialTitle },
            ogDescription: { property: 'og:description', content: description },
            ogSiteName: { property: 'og:site_name', content: seoConfig.siteName },
            ogType: { property: 'og:type', content: type },
            ogLocale: { property: 'og:locale', content: seoConfig.locale },
            ...(canonicalUrl ? { ogUrl: { property: 'og:url', content: canonicalUrl } } : {}),
            ogImage: { property: 'og:image', content: resolvedImage },
            ogImageAlt: { property: 'og:image:alt', content: imageAlt },
            twitterCard: { name: 'twitter:card', content: 'summary' },
            twitterTitle: { name: 'twitter:title', content: socialTitle },
            twitterDescription: { name: 'twitter:description', content: description },
            twitterImage: { name: 'twitter:image', content: resolvedImage },
            twitterImageAlt: { name: 'twitter:image:alt', content: imageAlt }
        },
        link: canonicalUrl ? { canonical: { rel: 'canonical', href: canonicalUrl } } : {},
        ...(isHomePage ? {
            script: {
                website: {
                    type: 'application/ld+json',
                    innerHTML: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebSite',
                        name: seoConfig.siteName,
                        url: seoConfig.siteUrl,
                        description,
                        inLanguage: seoConfig.language
                    }).replace(/</g, '\\u003c')
                }
            }
        } : {})
    };
}

/**
 * Adds the site name once, including when a page title already names the brand.
 *
 * @param {string} [title] - The page title.
 * @returns {string} - The document or social-preview title.
 */
export function formatPageTitle(title) {
    if (!title) {
        return seoConfig.siteName;
    }
    return title.includes(seoConfig.siteName) ? title : `${title} | ${seoConfig.siteName}`;
}
